import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  BadgeCheck,
  BookOpenCheck,
  Boxes,
  CalendarClock,
  Check,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  Download,
  FileCheck2,
  FileText,
  FolderLock,
  Home,
  Landmark,
  LayoutDashboard,
  Moon,
  MoreHorizontal,
  PenLine,
  Plus,
  RefreshCw,
  ScrollText,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  UserRound,
  UsersRound,
} from "lucide-react";
import { documentDefinitions } from "./data";
import { downloadText, getReviewFlags } from "./documents";
import { loadWorkspace, resetWorkspace, saveWorkspace } from "./storage";
import type { Asset, AssetKind, FundingStatus, PlanAnswers, Section, Status, Workspace } from "./types";

type Theme = "dark" | "light";
type Icon = typeof LayoutDashboard;

const themeKey = "stillwater-trust-theme";
const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const navigation: Array<{ id: Section; label: string; icon: Icon }> = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "builder", label: "Plan builder", icon: PenLine },
  { id: "people", label: "People", icon: UsersRound },
  { id: "assets", label: "Assets & funding", icon: CircleDollarSign },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "execution", label: "Execution", icon: ClipboardCheck },
  { id: "review", label: "Annual review", icon: CalendarClock },
  { id: "settings", label: "Workspace settings", icon: Settings },
];

function initialTheme(): Theme {
  const stored = localStorage.getItem(themeKey);
  if (stored === "dark" || stored === "light") return stored;
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function statusClass(status: Status | FundingStatus): string {
  return status.toLowerCase().replaceAll(" ", "-");
}

function formatUpdated(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

function AppIcon({ icon: Icon, size = 18 }: { icon: Icon; size?: number }) {
  return <Icon size={size} strokeWidth={1.8} aria-hidden="true" />;
}

function StatusMark({ status }: { status: Status }) {
  return (
    <span className={`status-mark ${statusClass(status)}`}>
      {status === "complete" ? <Check size={13} /> : status === "review" ? <ShieldCheck size={13} /> : <ChevronRight size={13} />}
      {status.replace("-", " ")}
    </span>
  );
}

function Shell({
  section,
  setSection,
  theme,
  toggleTheme,
  children,
}: {
  section: Section;
  setSection: (section: Section) => void;
  theme: Theme;
  toggleTheme: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <button className="brand" onClick={() => setSection("overview")} aria-label="Go to overview">
          <span className="brand-mark"><Archive size={18} /></span>
          <span>
            <strong>Stillwater</strong>
            <small>Trust workspace</small>
          </span>
        </button>
        <nav className="nav-list" aria-label="Primary navigation">
          {navigation.map(({ id, label, icon }) => (
            <button key={id} className={`nav-item ${section === id ? "active" : ""}`} onClick={() => setSection(id)}>
              <AppIcon icon={icon} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <button className="theme-toggle" onClick={toggleTheme} aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
          </button>
          <div className="privacy-note">
            <FolderLock size={16} />
            <span>Saved privately on this device</span>
          </div>
        </div>
      </aside>
      <main className="main-area">{children}</main>
    </div>
  );
}

function PageHeader({
  title,
  subtitle,
  updated,
  action,
}: {
  title: string;
  subtitle: string;
  updated: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      <div className="header-side">
        <span>Saved {formatUpdated(updated)}</span>
        {action}
      </div>
    </header>
  );
}

function ProgressRing({ value }: { value: number }) {
  return (
    <div className="progress-ring" style={{ "--progress": `${value * 3.6}deg` } as React.CSSProperties}>
      <div><strong>{value}%</strong><span>ready</span></div>
    </div>
  );
}

function readiness(workspace: Workspace) {
  const answered = Object.values(workspace.answers).filter(Boolean).length;
  const funded = workspace.assets.filter((asset) => asset.status === "Funded").length;
  const executed = workspace.execution.filter((item) => item.done).length;
  const flags = getReviewFlags(workspace).length;
  const percent = Math.min(100, Math.round(30 + answered * 1.5 + funded * 4 + executed * 4 - flags * 4));
  return { percent, funded, executed, flags };
}

function Overview({ workspace, setSection }: { workspace: Workspace; setSection: (section: Section) => void }) {
  const state = readiness(workspace);
  const totalValue = workspace.assets.reduce((sum, asset) => sum + asset.value, 0);
  const steps = [
    { title: "Build the plan", detail: "Core answers captured", status: "complete" as Status, icon: PenLine },
    { title: "Fund the trust", detail: `${state.funded} of ${workspace.assets.length} tracked items ready`, status: state.funded === workspace.assets.length ? "complete" : "in-progress" as Status, icon: CircleDollarSign },
    { title: "Sign and execute", detail: `${state.executed} of ${workspace.execution.length} actions complete`, status: state.executed === workspace.execution.length ? "complete" : "not-started" as Status, icon: FileCheck2 },
    { title: "Keep it current", detail: "Annual review available", status: workspace.reviews.length ? "complete" : "not-started" as Status, icon: CalendarClock },
  ];

  return (
    <>
      <PageHeader title="Your living trust workspace" subtitle="Nevada revocable living trust" updated={workspace.lastUpdated} action={<button className="primary-btn" onClick={() => setSection("builder")}>Continue plan <ChevronRight size={16} /></button>} />
      <section className="overview-hero zen-panel">
        <div>
          <h2>{workspace.answers.trustName}</h2>
          <p>A practical workspace for building, funding, executing, and maintaining your trust package.</p>
          <button className="text-btn" onClick={() => setSection("documents")}>Open trust package <ChevronRight size={15} /></button>
        </div>
        <ProgressRing value={state.percent} />
      </section>
      <section className="journey">
        {steps.map((step, index) => (
          <button className="journey-step" key={step.title} onClick={() => setSection((["builder", "assets", "execution", "review"] as Section[])[index])}>
            <span className={`journey-icon ${statusClass(step.status)}`}><AppIcon icon={step.icon} /></span>
            <span><strong>{step.title}</strong><small>{step.detail}</small></span>
            <ChevronRight size={16} />
          </button>
        ))}
      </section>
      <div className="overview-grid">
        <section className="zen-panel">
          <div className="section-heading"><div><h2>Trust package</h2><p>Draft documents and working checklists</p></div><button className="icon-btn" onClick={() => setSection("documents")} aria-label="Open documents"><MoreHorizontal size={18} /></button></div>
          <div className="document-list compact">
            {documentDefinitions.slice(0, 5).map((document) => (
              <div className="document-row" key={document.id}>
                <span className="list-icon"><ScrollText size={17} /></span>
                <span className="grow"><strong>{document.title}</strong><small>{document.category}</small></span>
                <StatusMark status={document.readiness} />
              </div>
            ))}
          </div>
        </section>
        <aside className="overview-side">
          <section className="zen-panel">
            <div className="section-heading"><div><h2>Funding progress</h2><p>{money.format(totalValue)} tracked</p></div><CircleDollarSign size={19} /></div>
            <div className="metric"><strong>{state.funded}</strong><span>of {workspace.assets.length} tracked items ready</span></div>
            <button className="secondary-btn full" onClick={() => setSection("assets")}>Review funding <ChevronRight size={15} /></button>
          </section>
          <section className="zen-panel review-card">
            <div className="section-heading"><div><h2>Plan readiness</h2><p>{state.flags ? "Review needed" : "No complexity flags"}</p></div><ShieldCheck size={19} /></div>
            <p>{state.flags ? `${state.flags} planning item${state.flags === 1 ? "" : "s"} should be resolved before execution.` : "Your current answers do not trigger an attorney-review checkpoint."}</p>
            <button className="text-btn" onClick={() => setSection("builder")}>View review items <ChevronRight size={15} /></button>
          </section>
        </aside>
      </div>
    </>
  );
}

const answerSections: Array<{ title: string; detail: string; fields: Array<keyof PlanAnswers> }> = [
  { title: "Trust identity", detail: "Set the plan name and home jurisdiction.", fields: ["trustName", "settlorName", "coSettlorName", "county", "residenceState"] },
  { title: "Trustee succession", detail: "Name the people who can manage the trust.", fields: ["successorTrustee", "backupTrustee"] },
  { title: "Beneficiaries and distribution", detail: "Describe who receives the remaining trust estate.", fields: ["primaryBeneficiary", "distributionPlan"] },
];

const fieldLabels: Record<keyof PlanAnswers, string> = {
  trustName: "Trust name", settlorName: "Settlor and initial trustee", coSettlorName: "Additional settlor, if any", county: "Nevada county", residenceState: "Residence state",
  successorTrustee: "First successor trustee", backupTrustee: "Backup successor trustee", primaryBeneficiary: "Primary beneficiary", distributionPlan: "Distribution instructions",
  minorBeneficiaries: "Minor beneficiaries", blendedFamily: "Blended family", specialNeedsBeneficiary: "Special-needs beneficiary", businessOwnership: "Business ownership",
  outOfStateRealEstate: "Real estate outside Nevada", nonUsPerson: "Non-US person involved", creditorProtectionGoal: "Asset-protection goal",
};

function PlanBuilder({ workspace, updateAnswers }: { workspace: Workspace; updateAnswers: (answers: Partial<PlanAnswers>) => void }) {
  const flags = getReviewFlags(workspace);
  const complexityKeys: Array<keyof PlanAnswers> = ["minorBeneficiaries", "blendedFamily", "specialNeedsBeneficiary", "businessOwnership", "outOfStateRealEstate", "nonUsPerson", "creditorProtectionGoal"];

  return (
    <>
      <PageHeader title="Plan builder" subtitle="Capture the decisions that shape your trust package." updated={workspace.lastUpdated} />
      <div className="content-grid builder-grid">
        <section className="stack">
          {answerSections.map((group) => (
            <div className="zen-panel form-panel" key={group.title}>
              <div className="section-heading"><div><h2>{group.title}</h2><p>{group.detail}</p></div><BadgeCheck size={19} /></div>
              <div className="field-grid">
                {group.fields.map((field) => (
                  <label className={field === "distributionPlan" ? "wide-field" : ""} key={field}>
                    <span>{fieldLabels[field]}</span>
                    {field === "distributionPlan" ? (
                      <textarea value={String(workspace.answers[field])} onChange={(event) => updateAnswers({ [field]: event.target.value })} />
                    ) : (
                      <input value={String(workspace.answers[field])} onChange={(event) => updateAnswers({ [field]: event.target.value })} />
                    )}
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="zen-panel form-panel">
            <div className="section-heading"><div><h2>Planning checkpoints</h2><p>Select every item that applies. Some answers require tailored review.</p></div><ShieldCheck size={19} /></div>
            <div className="check-grid">
              {complexityKeys.map((key) => (
                <label className="check-row" key={key}>
                  <input type="checkbox" checked={Boolean(workspace.answers[key])} onChange={(event) => updateAnswers({ [key]: event.target.checked })} />
                  <span><strong>{fieldLabels[key]}</strong><small>{key === "minorBeneficiaries" ? "Adds continuing-trust planning for younger beneficiaries." : "Routes the package through an attorney-review checkpoint."}</small></span>
                </label>
              ))}
            </div>
          </div>
        </section>
        <aside className="stack sticky-column">
          <div className="zen-panel review-card">
            <h2>Review needed</h2>
            {flags.length ? flags.map((flag) => <div className="flag-row" key={flag}><ShieldCheck size={16} /><span>{flag}</span></div>) : <p>No attorney-review checkpoints are currently triggered.</p>}
          </div>
          <div className="zen-panel note-card">
            <BookOpenCheck size={20} />
            <h2>Drafting boundary</h2>
            <p>This workspace prepares an organized draft package and execution plan. Resolve flagged items and review the final instrument before signing.</p>
          </div>
        </aside>
      </div>
    </>
  );
}

function People({ workspace }: { workspace: Workspace }) {
  return (
    <>
      <PageHeader title="People" subtitle="The roles that keep the trust operable through life changes." updated={workspace.lastUpdated} />
      <section className="zen-panel">
        <div className="section-heading"><div><h2>Trust roles</h2><p>These entries update as you edit the plan builder.</p></div><UsersRound size={19} /></div>
        <div className="people-list">
          {workspace.people.map((person) => (
            <div className="person-row" key={person.id}><span className="person-avatar">{person.name.split(" ").map((part) => part[0]).join("").slice(0, 2)}</span><span className="grow"><strong>{person.name}</strong><small>{person.role}</small><p>{person.detail}</p></span></div>
          ))}
        </div>
      </section>
    </>
  );
}

function Assets({ workspace, setWorkspace }: { workspace: Workspace; setWorkspace: React.Dispatch<React.SetStateAction<Workspace>> }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<AssetKind>("Banking");
  const [value, setValue] = useState("");
  const total = workspace.assets.reduce((sum, asset) => sum + asset.value, 0);
  const setStatus = (id: string, status: FundingStatus) => setWorkspace((current) => ({ ...current, assets: current.assets.map((asset) => asset.id === id ? { ...asset, status } : asset) }));
  const addAsset = () => {
    if (!name.trim()) return;
    setWorkspace((current) => ({ ...current, assets: [...current.assets, { id: crypto.randomUUID(), name: name.trim(), kind, value: Number(value) || 0, status: "Action needed" }] }));
    setName(""); setValue("");
  };
  return (
    <>
      <PageHeader title="Assets & funding" subtitle="Track the property coordinated with your trust and the action each asset needs." updated={workspace.lastUpdated} />
      <div className="funding-metrics">
        <div><span>Total tracked</span><strong>{money.format(total)}</strong></div>
        <div><span>Funded items</span><strong>{workspace.assets.filter((asset) => asset.status === "Funded").length}</strong></div>
        <div><span>Action needed</span><strong>{workspace.assets.filter((asset) => asset.status !== "Funded").length}</strong></div>
      </div>
      <section className="zen-panel">
        <div className="section-heading"><div><h2>Funding register</h2><p>Use the status control to keep transfer work visible.</p></div><CircleDollarSign size={19} /></div>
        <div className="asset-list">
          {workspace.assets.map((asset) => (
            <div className="asset-row" key={asset.id}>
              <span className="list-icon">{asset.kind === "Real estate" ? <Home size={17} /> : asset.kind === "Banking" ? <Landmark size={17} /> : <Boxes size={17} />}</span>
              <span className="grow"><strong>{asset.name}</strong><small>{asset.kind}</small></span>
              <span className="asset-value">{money.format(asset.value)}</span>
              <select value={asset.status} onChange={(event) => setStatus(asset.id, event.target.value as FundingStatus)} className={statusClass(asset.status)}>
                <option>Funded</option><option>Action needed</option><option>Review beneficiary</option>
              </select>
            </div>
          ))}
        </div>
      </section>
      <section className="zen-panel add-row">
        <input aria-label="Asset name" placeholder="Add an asset" value={name} onChange={(event) => setName(event.target.value)} />
        <select aria-label="Asset type" value={kind} onChange={(event) => setKind(event.target.value as AssetKind)}><option>Real estate</option><option>Banking</option><option>Brokerage</option><option>Business</option><option>Personal property</option><option>Digital assets</option></select>
        <input aria-label="Asset value" placeholder="Estimated value" inputMode="decimal" value={value} onChange={(event) => setValue(event.target.value)} />
        <button className="primary-btn" onClick={addAsset}><Plus size={16} /> Add asset</button>
      </section>
    </>
  );
}

function Documents({ workspace }: { workspace: Workspace }) {
  return (
    <>
      <PageHeader title="Documents" subtitle="Generate and download the current working package from your saved plan." updated={workspace.lastUpdated} />
      <div className="notice"><Sparkles size={17} /><span>Downloads are generated from your workspace data. Review drafts before execution and resolve any review checkpoints.</span></div>
      <section className="zen-panel">
        <div className="section-heading"><div><h2>Trust package</h2><p>Core drafts, companion-document checkpoints, and funding instructions.</p></div><ScrollText size={19} /></div>
        <div className="document-list">
          {documentDefinitions.map((document) => (
            <div className="document-row expanded" key={document.id}>
              <span className="list-icon"><FileText size={18} /></span>
              <span className="grow"><strong>{document.title}</strong><small>{document.category}</small><p>{document.description}</p></span>
              <StatusMark status={document.readiness} />
              <button className="secondary-btn" onClick={() => downloadText(workspace, document.id, document.title)}><Download size={15} /> Download</button>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function Execution({ workspace, setWorkspace }: { workspace: Workspace; setWorkspace: React.Dispatch<React.SetStateAction<Workspace>> }) {
  const toggle = (id: string) => setWorkspace((current) => ({ ...current, execution: current.execution.map((item) => item.id === id ? { ...item, done: !item.done } : item) }));
  return (
    <>
      <PageHeader title="Execution" subtitle="Turn the plan into an operating trust with a visible completion record." updated={workspace.lastUpdated} />
      <section className="zen-panel">
        <div className="section-heading"><div><h2>Sign and execute</h2><p>Complete each action in order and retain the final confirmations.</p></div><ClipboardCheck size={19} /></div>
        <div className="execution-list">
          {workspace.execution.map((item, index) => (
            <button className={`execution-row ${item.done ? "done" : ""}`} key={item.id} onClick={() => toggle(item.id)}>
              <span className="step-number">{item.done ? <Check size={16} /> : index + 1}</span>
              <span className="grow"><strong>{item.title}</strong><small>{item.detail}</small></span>
            </button>
          ))}
        </div>
      </section>
    </>
  );
}

function Review({ workspace, setWorkspace }: { workspace: Workspace; setWorkspace: React.Dispatch<React.SetStateAction<Workspace>> }) {
  const completeReview = () => setWorkspace((current) => ({ ...current, reviews: [...current.reviews, { completedAt: new Date().toISOString(), note: "Annual workspace review completed." }] }));
  return (
    <>
      <PageHeader title="Annual review" subtitle="Keep the package aligned with changes in family, property, and planning goals." updated={workspace.lastUpdated} action={<button className="primary-btn" onClick={completeReview}><RefreshCw size={15} /> Complete review</button>} />
      <div className="content-grid">
        <section className="zen-panel">
          <div className="section-heading"><div><h2>Review checklist</h2><p>Revisit these questions after a major event or at least annually.</p></div><CalendarClock size={19} /></div>
          {["Have any trustees or beneficiaries changed?", "Have you bought, sold, or refinanced real estate?", "Are bank and brokerage accounts coordinated with the plan?", "Have beneficiary designations been reviewed?", "Has your residence, family structure, or business ownership changed?", "Are signed originals and access instructions stored securely?"].map((item) => <div className="review-line" key={item}><Check size={15} /><span>{item}</span></div>)}
        </section>
        <section className="zen-panel">
          <div className="section-heading"><div><h2>Review history</h2><p>{workspace.reviews.length ? `${workspace.reviews.length} completed review${workspace.reviews.length === 1 ? "" : "s"}` : "No completed reviews yet"}</p></div><BookOpenCheck size={19} /></div>
          {workspace.reviews.length ? [...workspace.reviews].reverse().map((review) => <div className="history-row" key={review.completedAt}><strong>{new Date(review.completedAt).toLocaleDateString()}</strong><small>{review.note}</small></div>) : <p className="empty-copy">Complete a review to create the first maintenance record.</p>}
        </section>
      </div>
    </>
  );
}

function SettingsPage({ workspace, replaceWorkspace }: { workspace: Workspace; replaceWorkspace: (workspace: Workspace) => void }) {
  return (
    <>
      <PageHeader title="Workspace settings" subtitle="Manage the browser-local planning workspace." updated={workspace.lastUpdated} />
      <section className="zen-panel settings-panel">
        <FolderLock size={21} />
        <h2>Private local workspace</h2>
        <p>Your current plan is stored in this browser. No Cloudflare Worker or remote database is required for the operational local workflow.</p>
        <button className="danger-btn" onClick={() => replaceWorkspace(resetWorkspace())}><RefreshCw size={15} /> Reset sample workspace</button>
      </section>
    </>
  );
}

export function App() {
  const [section, setSection] = useState<Section>("overview");
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [workspace, setWorkspace] = useState<Workspace>(loadWorkspace);
  const updateWorkspace = useCallback<React.Dispatch<React.SetStateAction<Workspace>>>((action) => {
    setWorkspace((current) => {
      const next = typeof action === "function" ? action(current) : action;
      return { ...next, lastUpdated: new Date().toISOString() };
    });
  }, []);

  useEffect(() => { document.documentElement.dataset.theme = theme; localStorage.setItem(themeKey, theme); }, [theme]);
  useEffect(() => { saveWorkspace(workspace); }, [workspace]);

  const syncedPeople = useMemo(() => {
    const { answers } = workspace;
    return workspace.people.map((person) => {
      if (person.id === "settlor") return { ...person, name: answers.settlorName || "Unnamed settlor" };
      if (person.id === "successor") return { ...person, name: answers.successorTrustee || "Unnamed successor" };
      if (person.id === "backup") return { ...person, name: answers.backupTrustee || "Unnamed backup" };
      if (person.id === "beneficiary") return { ...person, name: answers.primaryBeneficiary || "Unnamed beneficiary" };
      return person;
    });
  }, [workspace.answers, workspace.people]);
  const visibleWorkspace = useMemo(() => ({ ...workspace, people: syncedPeople }), [workspace, syncedPeople]);

  const updateAnswers = (answers: Partial<PlanAnswers>) => updateWorkspace((current) => ({ ...current, answers: { ...current.answers, ...answers } }));
  const replaceWorkspace = (next: Workspace) => setWorkspace(next);
  let content: React.ReactNode;
  if (section === "overview") content = <Overview workspace={visibleWorkspace} setSection={setSection} />;
  else if (section === "builder") content = <PlanBuilder workspace={visibleWorkspace} updateAnswers={updateAnswers} />;
  else if (section === "people") content = <People workspace={visibleWorkspace} />;
  else if (section === "assets") content = <Assets workspace={visibleWorkspace} setWorkspace={updateWorkspace} />;
  else if (section === "documents") content = <Documents workspace={visibleWorkspace} />;
  else if (section === "execution") content = <Execution workspace={visibleWorkspace} setWorkspace={updateWorkspace} />;
  else if (section === "review") content = <Review workspace={visibleWorkspace} setWorkspace={updateWorkspace} />;
  else content = <SettingsPage workspace={visibleWorkspace} replaceWorkspace={replaceWorkspace} />;

  return <Shell section={section} setSection={setSection} theme={theme} toggleTheme={() => setTheme((current) => current === "dark" ? "light" : "dark")}><div className="content-wrap">{content}</div></Shell>;
}
